import AutorenewIcon from '@mui/icons-material/Autorenew'
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutlined'
import HelpOutlineIcon from '@mui/icons-material/HelpOutlined'
import PictureAsPdfOutlinedIcon from '@mui/icons-material/PictureAsPdfOutlined'
import RefreshIcon from '@mui/icons-material/Refresh'
import { Autocomplete, Checkbox, IconButton, InputAdornment, TextField, Tooltip } from '@mui/material'
import { createFilterOptions } from '@mui/material/Autocomplete'
import React from 'react'
import type { SearchAseguradoraDTO } from '../../../model/kartera-api-model'
import KarteraAdminTemplate from '../../../template/KarteraAdminTemplate'
import KarteraPageHeader from '../../../template/kartera/KarteraPageHeader'
import { CheckIcon, EyeIcon, EyeOffIcon } from '../../../template/kartera/icons'
import { RAMOS, RamoMark } from '../common/agent-key-common'
import { useAgentKeyFormModel } from './agent-key-form-model'

const filterInsurers = createFilterOptions<SearchAseguradoraDTO>({
    stringify: (option) => `${option.aseguradora ?? ''} ${option.abreviacion ?? ''}`,
})
const checkboxSx = { padding: '2px', color: 'rgb(var(--k-muted))', '&.Mui-checked': { color: 'rgb(var(--k-primary))' } }
const primaryBtnClass = 'inline-flex h-11 items-center justify-center gap-2 rounded-xl border-0 bg-k-primary px-[18px] text-sm font-bold !text-white hover:brightness-105 disabled:cursor-not-allowed disabled:opacity-40'
const outlineBtnClass = 'inline-flex h-11 items-center justify-center gap-2 rounded-xl border border-k-line bg-k-surface px-4 text-sm font-semibold text-k-ink hover:bg-k-hover'

const Capability = ({ on, icon, onLabel, offLabel }: { on: boolean, icon: React.ReactNode, onLabel: string, offLabel: string }) => (
    <span className={`inline-flex items-center gap-2 rounded-full px-3 py-1.5 text-[13px] font-semibold ${on ? 'bg-k-ok-soft text-k-ok' : 'bg-k-line-soft text-k-muted'}`}>
        {icon}
        {on ? onLabel : offLabel}
    </span>
)

const AgentKeyForm = () => {
    const model = useAgentKeyFormModel()
    const { form, change } = model

    return (
        <KarteraAdminTemplate loading={false} title={model.title}>
            <form onSubmit={model.save} className="relative mx-auto flex w-full max-w-[880px] flex-col gap-[18px]" autoComplete="off">
                {model.busy && (
                    <div className="absolute inset-0 z-40 flex items-start justify-center rounded-2xl bg-k-bg/80 pt-24" role="status">
                        <div className="flex flex-col items-center gap-3 rounded-2xl border border-k-line bg-k-surface px-8 py-6 shadow-k-pop">
                            <RefreshIcon className="animate-spin text-brand-indigo" />
                            <p className="m-0 text-sm font-semibold text-k-ink">{model.busyLabel}</p>
                        </div>
                    </div>
                )}

                <KarteraPageHeader
                    title={model.title}
                    description="Registra tu clave por aseguradora, los ramos que manejas y tus comisiones default."
                    backTo={model.backTo}
                    backLabel="Claves de agente"
                    actions={<span className="text-[13px] text-k-muted"><span className="text-k-danger">*</span> Obligatorio</span>}
                />

                {model.message && (
                    <div className={`flex items-center gap-3 rounded-[14px] px-4 py-3.5 text-sm ${model.message.type === 'error' ? 'bg-k-danger-soft text-k-danger-ink' : 'bg-k-ok-soft text-k-ok'}`}>
                        <ErrorOutlineIcon style={{ fontSize: 20 }} />
                        <p className="m-0">{model.message.message}</p>
                    </div>
                )}

                <section className="k-card">
                    <h2 className="k-card-title">Clave</h2>
                    <div className="grid grid-cols-1 items-start gap-x-6 gap-y-5 md:grid-cols-2">
                        <Autocomplete
                            fullWidth
                            options={model.insurers}
                            value={model.insurer}
                            openOnFocus
                            selectOnFocus
                            autoHighlight
                            autoSelect={false}
                            clearOnBlur
                            noOptionsText="Sin coincidencias"
                            getOptionLabel={(option) => option.aseguradora || ''}
                            getOptionKey={(option) => option.id}
                            isOptionEqualToValue={(option, current) => option.id === current.id}
                            filterOptions={filterInsurers}
                            onChange={(_, option) => change.aseguradoraId(option ? option.id : '')}
                            slotProps={{ paper: { className: 'v2-select-menu' } }}
                            renderInput={(params) => (
                                <TextField {...params} className="v2-field" variant="filled" fullWidth required label="Aseguradora" />
                            )}
                        />
                        <TextField
                            className="v2-field" variant="filled" fullWidth required
                            label="Clave de agente"
                            disabled={model.isEdit}
                            helperText={model.isEdit ? 'La clave no se puede cambiar; elimínala y regístrala de nuevo si es necesario.' : 'Máximo 15 caracteres.'}
                            value={form.clave}
                            onChange={(event) => change.clave(event.target.value)}
                        />
                    </div>
                    {model.insurer && (
                        <div className="flex flex-wrap gap-2">
                            <Capability on={Boolean(model.insurer.pdf)} icon={<PictureAsPdfOutlinedIcon style={{ fontSize: 16 }} />} onLabel="Cuenta con traducción de PDF" offLabel="No cuenta con traducción de PDF" />
                            <Capability on={model.syncAvailable} icon={<AutorenewIcon style={{ fontSize: 16 }} />} onLabel="Cuenta con sincronización automática" offLabel="No cuenta con sincronización automática" />
                        </div>
                    )}

                    <div className="flex flex-col gap-2">
                        <span className="text-[13px] font-semibold text-k-ink">Ramos que manejas con esta clave</span>
                        <div className="flex flex-wrap gap-2">
                            {RAMOS.map((ramo) => {
                                const on = form.ramos.includes(ramo.id)
                                return (
                                    <button
                                        key={ramo.id}
                                        type="button"
                                        aria-pressed={on}
                                        onClick={() => change.toggleRamo(ramo.id)}
                                        className={`inline-flex min-h-[40px] items-center gap-2 rounded-full border px-3.5 text-sm font-semibold ${on
                                            ? 'border-k-primary bg-k-accent-soft text-k-accent-ink'
                                            : 'border-k-line-strong bg-k-surface text-k-ink-2 hover:bg-k-hover'}`}
                                    >
                                        <RamoMark ramo={ramo.ramo} size={16} />
                                        {ramo.label}
                                    </button>
                                )
                            })}
                        </div>
                    </div>
                </section>

                <section className="k-card">
                    <h2 className="k-card-title">Sincronización automática</h2>
                    <label className={`flex items-center gap-3 ${model.syncAvailable ? 'cursor-pointer' : 'cursor-not-allowed opacity-60'}`}>
                        <Checkbox
                            sx={{ ...checkboxSx, '& .MuiSvgIcon-root': { fontSize: 32 } }}
                            disabled={!model.syncAvailable}
                            checked={form.sincronizacion}
                            onChange={(event) => change.sincronizacion(event.target.checked)}
                        />
                        <span className="flex flex-col gap-0.5">
                            <span className="text-sm font-bold text-k-ink">Habilitar sincronización automática</span>
                            <span className="text-xs text-k-muted">
                                {!model.insurer
                                    ? 'Selecciona una aseguradora para saber si está disponible.'
                                    : model.syncAvailable
                                        ? 'Descargamos tus pólizas del portal de la aseguradora de forma periódica.'
                                        : 'Esta aseguradora todavía no cuenta con sincronización automática.'}
                            </span>
                        </span>
                    </label>

                    {form.sincronizacion && (
                        <>
                            <div className="grid grid-cols-1 items-start gap-x-6 gap-y-5 md:grid-cols-2">
                                <TextField
                                    className="v2-field" variant="filled" fullWidth required
                                    label="Usuario del portal de la aseguradora"
                                    autoComplete="off"
                                    value={form.portalClave}
                                    onChange={(event) => change.portalClave(event.target.value)}
                                />
                                <TextField
                                    className="v2-field" variant="filled" fullWidth required
                                    label="Contraseña del portal"
                                    type={model.showPassword ? 'text' : 'password'}
                                    autoComplete="new-password"
                                    helperText={model.isEdit ? 'Por seguridad, captúrala de nuevo cada vez que guardes.' : undefined}
                                    value={form.portalPassword}
                                    onChange={(event) => change.portalPassword(event.target.value)}
                                    slotProps={{
                                        input: {
                                            endAdornment: (
                                                <InputAdornment position="end">
                                                    <IconButton size="small" aria-label={model.showPassword ? 'Ocultar contraseña' : 'Mostrar contraseña'} onClick={model.toggleShowPassword} className="text-k-muted-2">
                                                        {model.showPassword ? <EyeIcon /> : <EyeOffIcon />}
                                                    </IconButton>
                                                </InputAdornment>
                                            ),
                                        },
                                    }}
                                />
                            </div>
                            <div className="flex flex-col gap-2.5 rounded-[14px] border border-k-line bg-k-sunken px-4 py-3.5 text-[13px] leading-[1.55] text-k-ink-2">
                                <p className="m-0 font-bold text-k-ink">Reglas de sincronización</p>
                                <p className="m-0">La primera sincronización se realizará <strong className="text-k-ink">15 minutos</strong> después de guardar los datos de acceso al portal de la aseguradora. Después se repetirá según los días indicados en el plan que tengas contratado.</p>
                                <p className="m-0 font-bold text-k-ink">Importante</p>
                                <p className="m-0">
                                    Nos comprometemos a realizar el mayor esfuerzo posible por completar cada evento de manera satisfactoria; sin embargo, el servicio de sincronización se ofrece
                                    <strong className="text-k-ink"> sin garantías de ningún tipo</strong> al depender por completo de la <strong className="text-k-ink">disponibilidad y respuesta oportuna</strong> de las plataformas de las aseguradoras.
                                    Por esta razón, el costo del servicio no está relacionado de forma directa con el éxito de cada evento de sincronización, al ser un servicio opcional.
                                </p>
                            </div>
                        </>
                    )}
                </section>

                <section className="k-card">
                    <div className="flex items-center gap-2">
                        <h2 className="k-card-title">Comisiones default</h2>
                        <Tooltip title="Porcentaje de comisión por ramo que se propone al capturar una póliza; puedes cambiarlo en cada póliza.">
                            <HelpOutlineIcon className="text-k-muted-2" style={{ fontSize: 18 }} />
                        </Tooltip>
                    </div>
                    <div className="grid grid-cols-2 items-start gap-x-6 gap-y-5 md:grid-cols-4">
                        {RAMOS.map((ramo) => (
                            <TextField
                                key={ramo.id}
                                className="v2-field" variant="filled" fullWidth required
                                label={ramo.label}
                                value={form[ramo.comision]}
                                onChange={(event) => change.comision(ramo.comision, event.target.value)}
                                onFocus={(event) => event.target.select()}
                                slotProps={{
                                    htmlInput: { inputMode: 'numeric', style: { textAlign: 'right' } },
                                    input: { endAdornment: <InputAdornment position="end">%</InputAdornment> },
                                }}
                            />
                        ))}
                    </div>
                </section>

                <div className="flex flex-wrap items-center justify-end gap-2.5 pt-1">
                    <button type="button" className={outlineBtnClass} onClick={model.cancel}>Cancelar</button>
                    <button type="submit" disabled={model.busy || !model.canSave} className={primaryBtnClass}>
                        <CheckIcon />
                        Guardar clave
                    </button>
                </div>
            </form>
        </KarteraAdminTemplate>
    )
}

export default AgentKeyForm
